import {
  ChartFieldSpec,
  ChartSpec,
  ChartTooltipFieldSpec,
} from 'src/charting/spec/chartSpec.types'

export interface ChartSpecValidationResult {
  valid: boolean
  errors: string[]
}

const allowedTopLevelKeys = new Set([
  'version',
  'mark',
  'title',
  'description',
  'ariaLabel',
  'encoding',
])

const allowedEncodingKeys = new Set(['x', 'y', 'color', 'tooltip'])
const allowedXFieldKeys = new Set(['field', 'type', 'title', 'domain'])
const allowedYFieldKeys = new Set([
  'field',
  'type',
  'title',
  'stack',
  'valueFormat',
])
const allowedColorFieldKeys = new Set(['field', 'type', 'title', 'domain'])
const allowedTooltipFieldKeys = new Set([
  'field',
  'type',
  'title',
  'valueFormat',
])
const supportedFieldTypes = new Set([
  'nominal',
  'ordinal',
  'temporal',
  'quantitative',
])

const validateObjectKeys = (
  value: Record<string, unknown>,
  allowedKeys: Set<string>,
  path: string,
  errors: string[],
) => {
  Object.keys(value).forEach((key) => {
    if (!allowedKeys.has(key)) {
      errors.push(`${path}.${key} is not supported`)
    }
  })
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const validateString = (
  value: unknown,
  path: string,
  errors: string[],
): value is string => {
  if (typeof value === 'string' && value.trim().length > 0) return true
  errors.push(`${path} must be a non-empty string`)
  return false
}

const validateField = (
  value: unknown,
  path: string,
  allowedKeys: Set<string>,
  errors: string[],
): value is ChartFieldSpec => {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`)
    return false
  }

  validateObjectKeys(value, allowedKeys, path, errors)
  validateString(value.field, `${path}.field`, errors)

  if (!supportedFieldTypes.has(value.type as string)) {
    errors.push(`${path}.type "${String(value.type)}" is not supported`)
  }

  if (value.title !== undefined && typeof value.title !== 'string') {
    errors.push(`${path}.title must be a string`)
  }

  return errors.length === 0
}

const validateTooltip = (
  tooltip: unknown,
  errors: string[],
): tooltip is ChartTooltipFieldSpec[] => {
  if (tooltip === undefined) return true
  if (!Array.isArray(tooltip)) {
    errors.push('encoding.tooltip must be an array')
    return false
  }

  tooltip.forEach((field, index) => {
    validateField(
      field,
      `encoding.tooltip[${index}]`,
      allowedTooltipFieldKeys,
      errors,
    )
    if (
      isRecord(field) &&
      field.valueFormat !== undefined &&
      field.valueFormat !== 'number' &&
      field.valueFormat !== 'currencyCents'
    ) {
      errors.push(
        `encoding.tooltip[${index}].valueFormat "${String(field.valueFormat)}" is not supported`,
      )
    }
  })

  return true
}

export const validateChartSpec = (spec: unknown): ChartSpecValidationResult => {
  const errors: string[] = []

  if (!isRecord(spec)) {
    return { valid: false, errors: ['chart spec must be an object'] }
  }

  validateObjectKeys(spec, allowedTopLevelKeys, 'spec', errors)

  if (spec.version !== 1) {
    errors.push('version must be 1')
  }

  if (spec.mark !== 'bar') {
    errors.push(`mark "${String(spec.mark)}" is not supported`)
  }

  if (spec.title !== undefined && typeof spec.title !== 'string') {
    errors.push('title must be a string')
  }

  if (spec.description !== undefined && typeof spec.description !== 'string') {
    errors.push('description must be a string')
  }

  if (spec.ariaLabel !== undefined && typeof spec.ariaLabel !== 'string') {
    errors.push('ariaLabel must be a string')
  }

  if (!isRecord(spec.encoding)) {
    errors.push('encoding must be an object')
    return { valid: false, errors }
  }

  validateObjectKeys(spec.encoding, allowedEncodingKeys, 'encoding', errors)

  validateField(spec.encoding.x, 'encoding.x', allowedXFieldKeys, errors)
  validateField(spec.encoding.y, 'encoding.y', allowedYFieldKeys, errors)

  const x = spec.encoding.x
  if (isRecord(x)) {
    if (x.type === 'quantitative') {
      errors.push('encoding.x quantitative fields are not supported')
    }
    if (
      x.domain !== undefined &&
      (!Array.isArray(x.domain) ||
        !x.domain.every((item) => typeof item === 'string'))
    ) {
      errors.push('encoding.x.domain must be an array of strings')
    }
  }

  const y = spec.encoding.y
  if (isRecord(y)) {
    if (y.type !== 'quantitative') {
      errors.push('encoding.y must be quantitative')
    }
    if (y.stack !== undefined && y.stack !== 'diverging') {
      errors.push(`encoding.y.stack "${String(y.stack)}" is not supported`)
    }
    if (
      y.valueFormat !== undefined &&
      y.valueFormat !== 'number' &&
      y.valueFormat !== 'currencyCents'
    ) {
      errors.push(
        `encoding.y.valueFormat "${String(y.valueFormat)}" is not supported`,
      )
    }
  }

  const color = spec.encoding.color
  if (color !== undefined) {
    validateField(color, 'encoding.color', allowedColorFieldKeys, errors)
    if (isRecord(color)) {
      if (color.type !== 'nominal' && color.type !== 'ordinal') {
        errors.push('encoding.color must be nominal or ordinal')
      }
      if (
        color.domain !== undefined &&
        (!Array.isArray(color.domain) ||
          !color.domain.every((item) => typeof item === 'string'))
      ) {
        errors.push('encoding.color.domain must be an array of strings')
      }
    }
  }

  validateTooltip(spec.encoding.tooltip, errors)

  return { valid: errors.length === 0, errors }
}

export function assertValidChartSpec(spec: unknown): asserts spec is ChartSpec {
  const result = validateChartSpec(spec)
  if (!result.valid) {
    throw new Error(`Unsupported chart spec: ${result.errors.join('; ')}`)
  }
}
